import { useEffect, useState, useCallback } from "react";
import ImageSlider from "../../Components/ImageSlider";
import { QRCodeSVG } from "qrcode.react";
import Button from "../../Components/Button";
import { useToast } from "../../Components/Toast";
import { toPng } from "html-to-image";
import axios from "axios";
import { GET_INVITATION_API } from "../../Constant/api";
import { loadGoogleFont } from "../../Constant/fontLoader";
import { getContrastTextColor } from "../../Constant/colors";
import { days, formatTo12Hour } from "../../Constant/time";

const Invitation = () => {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [eventStyling, setEventStyling] = useState(null);
  const [monetaryDetails, setMonetaryDetails] = useState(null);
  const [guestInfo, setGuestInfo] = useState(null);

  const { toast } = useToast();

  const fetchInvitationData = useCallback(async (guestUuid) => {
    axios
      .get(GET_INVITATION_API, {
        params: { uuid: guestUuid },
      })
      .then((res) => {
        const guest = res.data.guest;
        const event = res.data.event;
        const customization = res.data.customizations;
        const monetary = res.data.monetary;

        if (customization.title_font && customization.title_font?.family) {
          loadGoogleFont(customization.title_font?.family);
        }

        if (customization.content_font && customization.content_font?.family) {
          loadGoogleFont(customization.content_font?.family);
        }

        setGuestInfo(guest);
        setEventDetails(event);
        setEventStyling(customization);
        setMonetaryDetails(monetary);
      })
      .catch((err) => {
        console.error("Error fetching invitation data:", err);
        toast("Failed to fetch invitation data", "error");
      });
  }, [toast]);

  const handleDownloadQRCode = () => {
    const node = document.getElementById("qr-code");
    toast("Downloading QR Code", "success");
    if (node) {
      toPng(node)
        .then((dataUrl) => {
          const link = document.createElement("a");
          link.download = `qrcode[${guestInfo.first_name}_${guestInfo.last_name}].png`;
          link.href = dataUrl;
          link.click();
        })
        .catch((error) => {
          console.error(error);
          toast("Failed to download QR code", "destructive");
        });
    } else {
      console.error('Could not find element with id "qr-code"');
    }
  };

  const normalizeColor = (color) => {
    if (!color) return "#5C4BF5";
    if (!color.startsWith("#")) return `#${color.toUpperCase()}`;
    return color.toUpperCase();
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("uuid")) {
      const guestUuid = params.get("uuid");
      fetchInvitationData(guestUuid);
    } else {
      toast("No invitation data found", "error");
    }
  }, [fetchInvitationData, toast]);

  return (
    <div>
      {eventDetails && eventStyling && guestInfo && (
        <div
          className="flex flex-col lg:flex-row w-full h-screen"
          style={{ color: eventStyling?.theme_color.toUpperCase() ?? "black" }}
        >
          {/* Left Section (Static Image on desktop, top image on mobile) */}
          <div className="w-full lg:w-[60%] h-screen">
            <div className="relative flex flex-col w-full h-screen">
              <ImageSlider imageList={eventDetails.image} />
              <div className="lg:hidden absolute bottom-0 z-10 text-white flex flex-col w-full h-full py-8 px-6 justify-end text-center items-center gap-3 shadow-[inset_0px_-50px_80px_70px_rgba(0,_0,_0,_0.3)]">
                <h1
                  className="text-[48px]"
                  style={{
                    fontFamily: eventStyling.title_font?.family ?? "inherit",
                  }}
                >
                  {eventDetails.name}
                </h1>
                <p
                  className="text-[12px]"
                  style={{
                    fontFamily: eventStyling.content_font?.family ?? "inherit",
                  }}
                >
                  {eventDetails.description}
                </p>
              </div>
            </div>
          </div>
          {/* Right Section */}
          <div className="w-full lg:w-[550px] flex-1 lg:overflow-y-auto">
            <div
              className="flex flex-col w-full min-h-screen h-[1024px] px-8 lg:px-[96px] items-center justify-center gap-12"
              style={{
                backgroundColor: eventStyling.background_image
                  ? "transparent"
                  : "white",
                color: eventStyling.text_color,
                fontFamily: eventStyling.font_family,
                backgroundImage: eventStyling.image
                  ? `url(${eventStyling.image})`
                  : null,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
              id="qr-code"
            >
              <div className="flex flex-col w-full gap-8">
                <p className="hidden lg:flex text-[16px] justify-center text-center">
                  - Welcome to -
                </p>
                <h1
                  className="hidden lg:flex text-[70px] justify-center text-center"
                  style={{
                    fontFamily: eventStyling.title_font?.family ?? "inherit",
                  }}
                >
                  {eventDetails.name}
                </h1>
                <div className="flex flex-col w-full items-center text-center justify-center gap-4 lg:gap-2">
                  <div className="flex flex-col gap-1 lg:gap-0">
                    <h2 className="flex flex-col text-[24px] font-semibold">
                      <span className="lg:hidden">
                        {days[new Date(eventDetails.date).getDay()]}
                      </span>
                      <span></span>
                      {eventDetails.date
                        ? new Date(eventDetails.date).toLocaleDateString(
                            "en-GB",
                            {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            }
                          )
                        : "No date set"}
                    </h2>
                    <h3 className="text-[14px] font-medium">
                      {formatTo12Hour(eventDetails.time)}
                    </h3>
                  </div>
                  <div className="border-2 w-6" />
                  <p className="text-[12px] lg:text-[16px]">
                    {eventDetails.venue}
                  </p>
                </div>
              </div>

              <div className="flex w-fit h-fit bg-white p-4 drop-shadow-xl">
                <QRCodeSVG
                  value={`${guestInfo.uuid}`}
                  bgColor={"white"}
                  fgColor={"black"}
                  size={"160"}
                  level={"H"}
                  imageSettings={{
                    height: 40,
                    width: 40,
                    src: "/images/logo.svg",
                  }}
                />
              </div>

              <p className="text-[12px]">
                {guestInfo.salutation && guestInfo.salutation + ". "}
                {guestInfo.first_name + " " + guestInfo.last_name}
              </p>
              <Button
                onClick={() => handleDownloadQRCode()}
                variant="custom"
                style={{
                  backgroundColor: normalizeColor(eventStyling?.theme_color),
                  color: getContrastTextColor(
                    normalizeColor(eventStyling?.theme_color)
                  ),
                }}
                className="active:scale-95 justify-center"
              >
                Download QR Code
              </Button>
            </div>
            {monetaryDetails && Boolean(monetaryDetails.monetary_enabled) && (
              <div className="flex flex-col w-full h-fit px-8 lg:px-[96px] py-[96px] lg:py-[128px] gap-12 text-center">
                <div className="flex flex-col w-full gap-[6px]">
                  <h2 className="text-[24px] font-semibold">
                    {monetaryDetails.monetary_title}
                  </h2>
                  <p className="text-[12px] mt-2">
                    {monetaryDetails.monetary_description}
                  </p>
                </div>

                {monetaryDetails.receive_option_id === 1 ? (
                  <div className="flex flex-col w-full gap-6">
                    <div className="grid grid-cols-2 w-full h-full gap-6">
                      {monetaryDetails.amount_options.map((amount, index) => (
                        <Button
                          variant="custom"
                          key={index}
                          style={{
                            border: `2px solid ${
                              selectedAmount === amount
                                ? normalizeColor(eventStyling?.theme_color)
                                : "#EFF0F2"
                            }`,
                          }}
                          onClick={() => setSelectedAmount(amount)}
                          className="justify-center"
                        >
                          {amount}
                        </Button>
                      ))}
                      <Button
                        variant="custom"
                        style={{
                          border: `2px solid ${
                            selectedAmount === "custom"
                              ? normalizeColor(eventStyling?.theme_color)
                              : "#EFF0F2"
                          }`,
                        }}
                        onClick={() => setSelectedAmount("custom")}
                        className="justify-center"
                      >
                        Custom
                      </Button>
                    </div>
                    {selectedAmount === "custom" && (
                      <div className="flex flex-col col-span-2 border border-[#EFF0F2] px-4 py-3 rounded-lg text-start gap-2">
                        <label>Enter an amount</label>
                        <div className="flex w-full text-[25px] items-center justify-between">
                          <span>RM</span>
                          <input
                            type="number"
                            value={customAmount}
                            onChange={(e) => setCustomAmount(e.target.value)}
                            placeholder="0"
                            className="bg-transparent text-end focus:outline-none active:outline-none w-full"
                          />
                        </div>
                      </div>
                    )}
                    <Button
                      variant="custom"
                      className="active:scale-95 justify-center"
                      style={{
                        backgroundColor: normalizeColor(
                          eventStyling?.theme_color
                        ),
                        color: getContrastTextColor(
                          normalizeColor(eventStyling?.theme_color)
                        ),
                      }}
                    >
                      Give Red Envelope
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-full">
                    <img
                      src={monetaryDetails.qr_code}
                      alt="Monetary Gift QR Code"
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Invitation;
